import React, { useState, useEffect } from 'react';
import { auth } from '../config/firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors';
import './FirebaseAuth.css';

const FirebaseAuth = ({ mode = 'signin', onSuccess, onError, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Update email if initialEmail changes (e.g., from verification redirect)
  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  // Clear confirm password when switching modes
  useEffect(() => {
    setConfirmPassword('');
    setError(null);
    setSuccess(null);
    setSignupSuccess(false);
  }, [mode]);

  // Refresh page after 3 seconds when signup is successful
  useEffect(() => {
    if (signupSuccess && mode === 'signup') {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [signupSuccess, mode]);

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Check for stored email verification error from previous attempt
    const storedError = sessionStorage.getItem('emailVerificationError');
    if (storedError) {
      sessionStorage.removeItem('emailVerificationError');
      setError(storedError);
    }

    // Log Firebase config right before login attempt
    // This helps debug auth/invalid-credential errors
    console.log('🔍 Firebase Config Check (Before Sign In):');
    console.log('Firebase projectId:', auth.app.options.projectId);
    if (process.env.NODE_ENV === 'development') {
      console.log('Firebase apiKey (FULL):', auth.app.options.apiKey || 'MISSING');
    } else {
      console.log('Firebase apiKey:', auth.app.options.apiKey ? `${auth.app.options.apiKey.substring(0, 20)}...` : 'MISSING');
    }
    console.log('Firebase authDomain:', auth.app.options.authDomain);
    console.log('Firebase appId:', auth.app.options.appId);
    console.log('Attempting to sign in with email:', email);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Note: We no longer block sign-in if email isn't verified
      // Users can sign in and use the app, but should verify their email
      // Email verification is still required for certain features (to be implemented later)
      
      if (onSuccess) onSuccess();
    } catch (err) {
      const errorMessage = getFirebaseErrorMessage(err);
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate password match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // Log Firebase config right before signup attempt
    console.log('🔍 Firebase Config Check (Before Sign Up):');
    console.log('Firebase projectId:', auth.app.options.projectId);
    if (process.env.NODE_ENV === 'development') {
      console.log('Firebase apiKey (FULL):', auth.app.options.apiKey || 'MISSING');
    } else {
      console.log('Firebase apiKey:', auth.app.options.apiKey ? `${auth.app.options.apiKey.substring(0, 20)}...` : 'MISSING');
    }
    console.log('Firebase authDomain:', auth.app.options.authDomain);
    console.log('Firebase appId:', auth.app.options.appId);
    console.log('Attempting to sign up with email:', email);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Store email in localStorage so we can use it after verification
      // This helps with auto-sign-in after email verification
      localStorage.setItem('pendingVerificationEmail', email);
      
      // Send email verification link
      // Note: For email/password users, we use sendEmailVerification (not sendSignInLinkToEmail)
      // sendSignInLinkToEmail is only for passwordless authentication
      const actionCodeSettings = {
        url: `${window.location.origin}/verify-email`,
        handleCodeInApp: false, // Set to false for regular email verification
      };
      
      await sendEmailVerification(user, actionCodeSettings);
      
      // Show success state instead of immediately calling onSuccess
      setSignupSuccess(true);
      
      // Don't call onSuccess yet - user needs to verify email first
      // The modal will show a success message instead
      // Note: User is still signed in at this point, so if they verify immediately
      // they'll be automatically signed in
    } catch (err) {
      const errorMessage = getFirebaseErrorMessage(err);
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    // Log Firebase config right before Google sign-in attempt
    console.log('🔍 Firebase Config Check (Before Google Sign In):');
    console.log('Firebase projectId:', auth.app.options.projectId);
    if (process.env.NODE_ENV === 'development') {
      console.log('Firebase apiKey (FULL):', auth.app.options.apiKey || 'MISSING');
    } else {
      console.log('Firebase apiKey:', auth.app.options.apiKey ? `${auth.app.options.apiKey.substring(0, 20)}...` : 'MISSING');
    }
    console.log('Firebase authDomain:', auth.app.options.authDomain);
    console.log('Firebase appId:', auth.app.options.appId);

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      if (onSuccess) onSuccess();
    } catch (err) {
      const errorMessage = getFirebaseErrorMessage(err);
      setError(errorMessage);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Check what sign-in methods are available for this email
      const signInMethods = await fetchSignInMethodsForEmail(auth, resetEmail);
      
      // If the user only has Google sign-in, they can't reset password
      if (signInMethods.length === 1 && signInMethods[0] === 'google.com') {
        setError('This email is registered with Google sign-in. Please sign in with Google instead of using password reset.');
        setLoading(false);
        return;
      }
      
      // If user has both Google and password, still allow password reset
      // (they might want to reset their password even if they usually use Google)
      
      await sendPasswordResetEmail(auth, resetEmail);
      setError(null);
      setSuccess('Password reset email sent! Check your inbox.');
      // Clear success message and close reset form after 3 seconds
      setTimeout(() => {
        setSuccess(null);
        setShowPasswordReset(false);
        setResetEmail('');
      }, 3000);
    } catch (err) {
      const errorMessage = getFirebaseErrorMessage(err);
      setError(errorMessage);
      setSuccess(null);
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner after signup (replaced "Check Your Email" screen)
  // STASHED CODE: Original "Check Your Email" screen is commented below for future use
  if (signupSuccess && mode === 'signup') {
    return (
      <div className="firebase-auth">
        <div className="signup-success">
          <div className="loading-spinner"></div>
          <h2>Creating Your Account</h2>
          <p>Please wait while we set up your account...</p>
        </div>
      </div>
    );
  }

  /* STASHED: Original "Check Your Email" screen code - uncomment to restore
  if (signupSuccess && mode === 'signup') {
    return (
      <div className="firebase-auth">
        <div className="signup-success">
          <div className="success-icon">✓</div>
          <h2>Check Your Email</h2>
          <p>We've sent a verification email to <strong>{email}</strong></p>
          <p className="success-instructions">
            Please click the link in the email to verify your account. Once verified, you can sign in.
          </p>
          <div className="success-actions">
            <button 
              type="button"
              onClick={async () => {
                const currentUser = auth.currentUser;
                if (currentUser) {
                  try {
                    setLoading(true);
                    const actionCodeSettings = {
                      url: `${window.location.origin}/verify-email`,
                      handleCodeInApp: false,
                    };
                    await sendEmailVerification(currentUser, actionCodeSettings);
                    setError(null);
                    alert('Verification email resent! Check your inbox.');
                  } catch (err) {
                    setError(getFirebaseErrorMessage(err));
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              disabled={loading}
              className="secondary-button"
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </button>
            <button 
              type="button"
              onClick={() => {
                setSignupSuccess(false);
                setEmail('');
                setPassword('');
                setConfirmPassword('');
                setError(null);
                // Switch to signin mode
                if (onSuccess) {
                  // This will be handled by parent component
                }
              }}
              className="link-button"
            >
              Back to Sign In
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  }
  */

  if (showPasswordReset) {
    return (
      <div className="firebase-auth">
        <h2>Reset Password</h2>
        <form onSubmit={handlePasswordReset}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <button type="submit" disabled={loading} className="primary-button">
            {loading ? 'Sending...' : 'Send Reset Email'}
          </button>
          <button 
            type="button" 
            onClick={() => {
              setShowPasswordReset(false);
              setError(null);
              setSuccess(null);
              setResetEmail('');
            }}
            className="secondary-button"
          >
            Back to Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="firebase-auth">
      <h2>{mode === 'signin' ? 'Sign In' : 'Create Account'}</h2>
      
      <form onSubmit={mode === 'signin' ? handleEmailSignIn : handleEmailSignUp}>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="Enter your email"
          />
        </div>
        
        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Enter your password"
            minLength={6}
          />
        </div>
        
        {mode === 'signup' && (
          <div className="form-group">
            <label>Confirm</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm your password"
              minLength={6}
            />
          </div>
        )}
        
        {error && <div className="error-message">{error}</div>}
        
        <button type="submit" disabled={loading} className="primary-button">
          {loading ? 'Please wait...' : (mode === 'signin' ? 'Sign In' : 'Create Account')}
        </button>
      </form>

      <div className="divider">
        <span>OR</span>
      </div>

      <button 
        onClick={handleGoogleSignIn} 
        disabled={loading}
        className="google-button"
      >
        <svg width="18" height="18" viewBox="0 0 18 18">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.96-2.184l-2.908-2.258c-.806.54-1.837.86-3.052.86-2.347 0-4.33-1.584-5.04-3.71H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
          <path fill="#FBBC05" d="M3.96 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.348 6.173 0 7.55 0 9s.348 2.827.957 4.042l3.003-2.332z"/>
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.96 7.29C4.67 5.163 6.653 3.58 9 3.58z"/>
        </svg>
        Sign in with Google
      </button>

      {mode === 'signin' && (
        <div className="auth-footer">
          <button 
            type="button"
            onClick={() => setShowPasswordReset(true)}
            className="link-button"
          >
            Forgot password?
          </button>
        </div>
      )}
    </div>
  );
};

export default FirebaseAuth;

